import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, Event, NavigationEnd, Params, Route, Router } from '@angular/router';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { find as _find, isEqual } from 'lodash';
import { DsBreakpoints, Icons, IDsBreadcrumbItem } from '@levelaccess/design-system';

import {
  INavigationContextItem,
  INavItemFilterArgs,
  INavItemRouterLinkArgs,
  INavMenuItem,
  INavMenuItemFilter,
  INavItemIsAvailableArgs,
  NavigationContext,
  NavigationItem,
  NavigationRoutingTree,
  INavItemOnClickArgs,
  INavItemLabelArgs,
} from './nav-structure';
import { SharedCommonUtility } from '../../../../shared/utils/common.utility';
import { UserAclService } from '../user-acl.service';
import { FeatureFlagService } from '../feature-flag/feature-flag.service';
import { AclSecurityAdapter } from '../acl.service';
import { AngularUtility } from '../../utility/angular.utility';
import { Api } from '../../../../shared/constants/api';
import { AppConfigService } from '../app-config.service';
import { WindowService } from '../window.service';
import { TenantPackageService } from '../tenant-package.service';
import { UserService } from '../user.service';
import { IUserServerResponse } from '../../../../shared/interfaces/user.interface';
import { $user } from '../../../../shared/constants/user';
import { ILinkedPropertyData, LinkedPropertyUtility } from '../../../../shared/utils/linked-property.utility';
import { UserDigitalPropertyService } from '../user-digital-property.service';
import { SecurityEntityLevel } from '../../../../shared/constants/security-group';
import { IRequiredSecurity } from '../../../../shared/interfaces/security.interface';
import { ITenantInfo } from '../../../../shared/interfaces/tenant.interface';
import { IDigitalPropertyListItem, IDigitalPropertyType } from '../../../../shared/interfaces/digital-property.interface';
import {
  $digitalProperty,
  $digitalPropertyType,
  DigitalPropertyTypeNames,
} from '../../../../shared/constants/digital-properties';
import { $workspace } from '../../../../shared/constants/workspace';
import { $tenant } from '../../../../shared/constants/tenant';
import { DigitalPropertyService } from '../digital-property.service';
import { LastVisitedLocationService } from './last-visited-location.service';
import { ILastVisitedLocation } from '../../interfaces/last-visited.interface';
import { DigitalPropertyTypeUtility } from '../../../../shared/utils/digital-property-type.utility';
import { UserOptinService } from '../user-optin.service';
import { FeatureFlagCollection } from '../../../../shared/interfaces/feature-flag.interface';
import { UserOptinFeatures } from '../../constants/user-optin';
import { PackageNames } from '../../../../shared/constants/packaging';
import { AcademyService } from '../academy.service';
import { AiService } from '../ai.service';
import { ScimConfigService } from '../scim-config.service';

type InternalMenusObservable = [
  filterArgs: INavItemFilterArgs,
  labelArgs: INavItemLabelArgs,
  isAvailableArgs: INavItemIsAvailableArgs,
  routerLinkArgs: INavItemRouterLinkArgs,
  onClickArgs: INavItemOnClickArgs,
  user: IUserServerResponse,
  params: Params,
  adapter: AclSecurityAdapter,
  isMobileView: boolean,
  featureFlagsChanged: void,
  lastActivatedRoute: ActivatedRoute,
];

@Injectable({
  providedIn: 'root',
})
export class NavService implements OnDestroy {
  private subscriptions: Subscription = new Subscription();
  private lastActivatedRoute$: Observable<ActivatedRoute>;
  private topLevelMenuItems$: Observable<INavMenuItem[]>;
  private activeDigitalPropertyFromQueryParams$: Observable<IDigitalPropertyListItem | undefined>;

  public isVerticalNavigationEnabled$: Observable<boolean>;
  public tenantNavigationContextItem$: Observable<INavigationContextItem>;
  public workspaceNavigationContextItem$: Observable<INavigationContextItem>;
  public digitalPropertyNavigationContextItem$: Observable<INavigationContextItem>;
  public currentNavigationContext$: Observable<NavigationContext>;
  public breadcrumbs$: Observable<IDsBreadcrumbItem[]>;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private userAclService: UserAclService,
    private featureFlagService: FeatureFlagService,
    private appConfigService: AppConfigService,
    private windowService: WindowService,
    private userService: UserService,
    private scimConfigService: ScimConfigService,
    private tenantPackageService: TenantPackageService,
    private digitalPropertyService: DigitalPropertyService,
    private userDigitalPropertyService: UserDigitalPropertyService,
    private lastVisitedService: LastVisitedLocationService,
    private userOptinService: UserOptinService,
    private academyService: AcademyService,
    private aiService: AiService,
  ) {
    this.buildLastActivatedRouteObservable();
    this.buildVerticalNavigationEnabledObservable();
    this.createInternalMenusObservable();
    this.subscribeToRouteChanges();
    this.buildActiveDigitalPropertyFromQueryParams();
    this.buildNavigationContextItems();
    this.buildNavigationContext();
  }

  public static getMenuItemContext(menuItem: INavMenuItem): NavigationContext {
    if (SharedCommonUtility.notNullish(menuItem?.context)) {
      return menuItem.context;
    }
    if (SharedCommonUtility.notNullish(menuItem?.parent)) {
      return NavService.getMenuItemContext(menuItem.parent);
    }
    return null;
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private buildLastActivatedRouteObservable(): void {
    const onlyWhenNavigationEnds = (event: Event): event is NavigationEnd => {
      return event instanceof NavigationEnd;
    };

    this.lastActivatedRoute$ = this.router.events.pipe(
      filter(onlyWhenNavigationEnds),
      tap((event: NavigationEnd): void => {
        const url: string = event.urlAfterRedirects;
        const urlWithoutParams: string = url.split('?')[0];
        const queryParams: Params = this.activatedRoute.snapshot.queryParams;
        const lastVisitedLocation: ILastVisitedLocation = {
          path: urlWithoutParams,
          queryParams: queryParams,
        };
        this.lastVisitedService.saveLastVisitedLocation(this.userService.getStoredUserId(), lastVisitedLocation);
      }),
      map((): ActivatedRoute => this.activatedRoute),
      startWith(this.activatedRoute),
      AngularUtility.shareRef(),
    );
  }

  private createInternalMenusObservable(): void {
    this.topLevelMenuItems$ = combineLatest([
      this.getNavItemFilterArgs(),
      this.getNavItemLabelArgs(),
      this.getNavItemIsAvailableArgs(),
      this.getNavItemRouterLinkArgs(),
      this.getNavItemOnClickArgs(),
      this.userService.userDataChanged$,
      this.activatedRoute.queryParams,
      this.userAclService.createCheckAccessForCurrentUser(),
      this.windowService.observeBreakpoint(DsBreakpoints.Large),
      this.featureFlagService.flagsChange$,
      this.lastActivatedRoute$,
    ]).pipe(
      map(
        ([
          filterArgs,
          labelArgs,
          isAvailableArgs,
          routerLinkArgs,
          onClickArgs,
          user,
          params,
          adapter,
          isMobileView,
        ]: InternalMenusObservable): INavMenuItem[] => {
          const isItemClickable = (item: INavMenuItem): boolean => {
            const hasRouterLink = Boolean(item.routerLink) || Boolean(item.routerLinkFn);
            const hasExternalLink = Boolean(item.externalLink) || Boolean(item.externalLinkFn);
            const hasOnClick = Boolean(item.onClick) || Boolean(item.onClickFn);
            return hasRouterLink || hasExternalLink || hasOnClick;
          };

          const setAvailableStateAndParent = (item: INavMenuItem, parent?: INavMenuItem): INavMenuItem => {
            const mappedItem: INavMenuItem = {
              ...item,
              isAvailable: true,
              children: [],
              parent: SharedCommonUtility.notNullishOrEmpty(parent) ? parent : undefined,
            };

            if (mappedItem.tenantlessOnly) {
              mappedItem.isAvailable = !this.appConfigService.isTenantEnabled();
            }

            if (mappedItem.isAvailable && mappedItem.tenantOnly) {
              mappedItem.isAvailable = this.appConfigService.isTenantEnabled();
            }

            if (mappedItem.isAvailable && SharedCommonUtility.notNullishOrEmpty(mappedItem.featureFlag)) {
              const negated: boolean = mappedItem.featureFlag[0] === '!';
              const featureFlag: string = negated ? mappedItem.featureFlag.slice(1) : mappedItem.featureFlag;
              const value: boolean = this.featureFlagService.variation(featureFlag, true);

              mappedItem.isAvailable = negated ? !value : value;
            }

            if (mappedItem.isAvailable && SharedCommonUtility.notNullish(item.requiredSecurity)) {
              const isAdmin: boolean = !this.appConfigService.isTenantEnabled();

              const availabilityCheck = (requiredSecurity: IRequiredSecurity): boolean => {
                const availability: boolean = adapter
                  .useFunctionalActions(requiredSecurity.functionalActions)
                  .useOptions({ requireFunctionalActions: true })
                  .useRequiredSecurity(requiredSecurity, isAdmin)
                  .check();
                adapter.reset();
                return availability;
              };

              mappedItem.isAvailable = Array.isArray(item.requiredSecurity)
                ? item.requiredSecurity.some(availabilityCheck)
                : availabilityCheck(item.requiredSecurity);
            }

            if (mappedItem.isAvailable && !isMobileView && mappedItem.mobileOnly) {
              mappedItem.isAvailable = false;
            }

            if (mappedItem.isAvailable && SharedCommonUtility.notNullish(item.isAvailableFn)) {
              mappedItem.isAvailable = item.isAvailableFn(isAvailableArgs);
            }

            if (mappedItem.isAvailable && SharedCommonUtility.notNullish(item.isHiddenFn)) {
              mappedItem.isHidden = item.isHiddenFn(isAvailableArgs);
            }

            if (mappedItem.isAvailable && item.restrictByWorkspace) {
              const linkedPropertyData: ILinkedPropertyData = LinkedPropertyUtility.fromLinkedPropertyQueryParam(params);
              mappedItem.isAvailable = user?.[$user.workspacesIds]?.includes(linkedPropertyData[Api.workspaceId]);
            }

            if (mappedItem.isAvailable && SharedCommonUtility.notNullish(item.labelFn)) {
              mappedItem.label = item.labelFn(labelArgs);
            }

            if (mappedItem.isAvailable && SharedCommonUtility.notNullish(item.routerLinkFn)) {
              mappedItem.routerLink = item.routerLinkFn(routerLinkArgs);
            }

            if (mappedItem.isAvailable && SharedCommonUtility.notNullish(item.onClickFn)) {
              mappedItem.onClick = item.onClickFn(onClickArgs);
            }

            if (mappedItem.isAvailable && SharedCommonUtility.notNullishOrEmpty(item.children)) {
              mappedItem.children = item.children.map((child: INavMenuItem): INavMenuItem =>
                setAvailableStateAndParent(child, mappedItem),
              );
              const firstAvailableChild: INavMenuItem = mappedItem.children.find(
                (child: INavMenuItem, index: number): boolean => {
                  if (!child.isAvailable || !isItemClickable(child)) {
                    return false;
                  }

                  if (!child.isHidden) {
                    return true;
                  }

                  // Routing-only children (e.g. portfolio) are hidden in nav but still define the parent routerLink.
                  return SharedCommonUtility.isNullish(item.children[index]?.isHiddenFn);
                },
              );
              const hasOverrideLink: boolean =
                Boolean(mappedItem.overrideRouterLink) ??
                mappedItem.children.some(
                  (child: INavMenuItem): boolean => child.isAvailable && child.routerLink === mappedItem.overrideRouterLink,
                );

              if (SharedCommonUtility.notNullish(firstAvailableChild)) {
                mappedItem.isAvailable = true;

                // if the nav item has children, points its routerLink to the routerLink of its first available child by default
                mappedItem.routerLink = hasOverrideLink ? mappedItem.overrideRouterLink : firstAvailableChild.routerLink;
              } else {
                mappedItem.isAvailable = isItemClickable(mappedItem);
              }
            }

            return mappedItem;
          };

          const url: string = this.router.routerState.snapshot.url;
          const checkIfMenuItemIsActiveAndMarkIt = (menuItems: INavMenuItem[]): void => {
            let bestMatch: {
              item: INavMenuItem;
              segmentCount: number;
            } | null = null;

            const findBestMatchItem = (menuItem: INavMenuItem): void => {
              const urlWithoutParams: string = url.split('?')[0];
              const urlSegments: string[] = urlWithoutParams.split('/').filter(Boolean);
              const menuItemSegments: string[] = menuItem.routerLink?.split('/').filter(Boolean) || [];

              const isExactMatch =
                menuItem.routerLink && urlSegments.length === menuItemSegments.length && urlWithoutParams === menuItem.routerLink;

              const isPartialMatch =
                menuItem.routerLink &&
                urlSegments.length >= menuItemSegments.length &&
                menuItemSegments.every((segment: string, index: number) => urlSegments[index] === segment);

              if (menuItem.isAvailable && (isExactMatch || isPartialMatch)) {
                if (!bestMatch || menuItemSegments.length > bestMatch.segmentCount) {
                  bestMatch = {
                    item: menuItem,
                    segmentCount: menuItemSegments.length,
                  };
                }
              }
            };

            const processMenuItems = (items: INavMenuItem[]): void => {
              items.forEach((menuItem: INavMenuItem): void => {
                if (SharedCommonUtility.notNullishOrEmpty(menuItem.children)) {
                  processMenuItems(menuItem.children);
                } else {
                  menuItem.isActive = false;
                  findBestMatchItem(menuItem);
                }
              });
            };

            const markMenuItemAsActive = (menuItem: INavMenuItem): void => {
              menuItem.isActive = true;
              if (SharedCommonUtility.notNullishOrEmpty(menuItem.parent)) {
                markMenuItemAsActive(menuItem.parent);
              }
            };

            processMenuItems(menuItems);

            if (bestMatch) {
              markMenuItemAsActive(bestMatch.item);
            }
          };

          const filterMenuItems = (menuItems: INavMenuItem[]): INavMenuItem[] => {
            return menuItems.filter((menuItem: INavMenuItem): boolean => {
              if (SharedCommonUtility.notNullishOrEmpty(menuItem?.children)) {
                menuItem.children = filterMenuItems(menuItem.children);
              }

              if (SharedCommonUtility.isNullishOrEmpty(menuItem.filters)) {
                return true;
              }

              return menuItem.filters.every((filterFn: INavMenuItemFilter) => filterFn(filterArgs));
            });
          };

          const topLevelMenuItems: INavMenuItem[] = this.navigationRoutingTree.map((item: INavMenuItem): INavMenuItem =>
            setAvailableStateAndParent(item),
          );
          const filteredMenuItems: INavMenuItem[] = filterMenuItems(topLevelMenuItems);
          checkIfMenuItemIsActiveAndMarkIt(filteredMenuItems);
          return filteredMenuItems;
        },
      ),
      AngularUtility.shareRef(),
    );
  }

  private getNavItemLabelArgs(): Observable<INavItemLabelArgs> {
    return this.isVerticalNavigationEnabled$.pipe(
      map((isVerticalNavigationEnabled: boolean): INavItemLabelArgs => ({
        isVerticalNavigationEnabled,
        featureFlagService: this.featureFlagService,
      })),
    );
  }

  private getNavItemIsAvailableArgs(): Observable<INavItemIsAvailableArgs> {
    return combineLatest([
      this.userService.userDataChanged$,
      this.tenantPackageService.tenantPackageChange$,
      this.userDigitalPropertyService.isWebDigitalProperty$,
      this.userDigitalPropertyService.isMobileDigitalProperty$,
      this.academyService.isAcademyAvailable$(),
      this.aiService.aiEnabledForTenant$,
      this.scimConfigService.isScimEnabled$(),
    ]).pipe(
      map(
        ([user, packageName, isWebDp, isMobileDp, isAcademyAvailable, isAiEnabled, isScimEnabled]: [
          IUserServerResponse,
          PackageNames,
          boolean,
          boolean,
          boolean,
          boolean,
          boolean,
        ]): INavItemIsAvailableArgs => ({
          user,
          packageName,
          isWebDp,
          isMobileDp,
          isAcademyAvailable,
          isAiEnabled,
          isScimEnabled,
          appConfigService: this.appConfigService,
          featureFlagService: this.featureFlagService,
        }),
      ),
    );
  }

  private getNavItemFilterArgs(): Observable<INavItemFilterArgs> {
    return this.isVerticalNavigationEnabled$.pipe(
      map((isVerticalNavigationEnabled: boolean): INavItemFilterArgs => ({ isVerticalNavigationEnabled })),
    );
  }

  private getNavItemRouterLinkArgs(): Observable<INavItemRouterLinkArgs> {
    return this.activatedRoute.queryParams.pipe(
      map((queryParams: Params): INavItemRouterLinkArgs => ({
        queryParams,
        featureFlagService: this.featureFlagService,
      })),
    );
  }

  private getNavItemOnClickArgs(): Observable<INavItemOnClickArgs> {
    return of({ academyService: this.academyService });
  }

  private subscribeToRouteChanges(): void {
    const getRoute = (value: [ActivatedRoute, INavMenuItem[]]): ActivatedRoute => {
      let _route: ActivatedRoute = value[0];
      const topLevelMenuItems: INavMenuItem[] = value[1];

      const getRouteMenuItem = (menuItems: INavMenuItem[], route: string): INavMenuItem => {
        return menuItems.reduce((found: INavMenuItem, item: INavMenuItem): INavMenuItem => {
          if (found) {
            return found;
          }
          if (item.children && item.children.length > 0) {
            return getRouteMenuItem(item.children, route);
          }
          return item.routerLink === route ? item : null;
        }, null);
      };
      const routeList: ActivatedRoute[] = [_route];
      while (_route.firstChild) {
        routeList.push(_route.firstChild);
        _route = _route.firstChild;
      }

      let currRoute: ActivatedRoute;
      while (routeList.length > 0) {
        currRoute = routeList.pop();
        const path: string = this.getFullPathFromActivatedRoute(currRoute);
        const pathWithoutDynamicSegments: string = this.getPathWithoutDynamicSegments(path);
        let routeMenuItem: INavMenuItem = getRouteMenuItem(topLevelMenuItems, pathWithoutDynamicSegments);
        if (SharedCommonUtility.notNullish(routeMenuItem) && routeMenuItem.isAvailable) {
          return currRoute;
        }
        if (SharedCommonUtility.notNullish(routeMenuItem)) {
          let firstAvailableRoute: INavMenuItem;
          while (SharedCommonUtility.isNullish(firstAvailableRoute)) {
            firstAvailableRoute = routeMenuItem.parent.children.find((child: INavMenuItem): boolean => child.isAvailable);
            routeMenuItem = routeMenuItem.parent;
          }
          this.router.navigate([firstAvailableRoute.routerLink]).then();
          break;
        }
      }
      return currRoute;
    };

    this.subscriptions.add(this.lastActivatedRoute$.pipe(withLatestFrom(this.topLevelMenuItems$)).subscribe(getRoute));
  }

  private getFullPathFromActivatedRoute(route: ActivatedRoute): string {
    return (
      '/' +
      route?.pathFromRoot
        ?.filter((v: ActivatedRoute): Route => v.routeConfig)
        .map((v: ActivatedRoute): string => v.routeConfig.path)
        .join('/')
    );
  }

  private getPathWithoutDynamicSegments(path: string): string {
    let baseRoute: string;
    if (path.indexOf(':') !== -1) {
      const routeBeforeColon = path.split(':')[0];
      if (routeBeforeColon.endsWith('/')) {
        baseRoute = routeBeforeColon.slice(0, -1);
      } else {
        baseRoute = routeBeforeColon;
      }
    } else {
      baseRoute = path;
    }
    return baseRoute;
  }

  private getAvailableSubmenusForItem(triggerItem: NavigationItem, selectedItem: INavMenuItem): INavMenuItem[] {
    const findSubmenuItem = (item: INavMenuItem): INavMenuItem => {
      if (SharedCommonUtility.isNullish(item)) {
        return undefined;
      }
      if (item.id === triggerItem) {
        return item;
      }

      const activeChildren: INavMenuItem[] = item.children?.filter((child: INavMenuItem): boolean => child.isActive) ?? [];
      for (const child of activeChildren) {
        const foundItem: INavMenuItem = findSubmenuItem(child);
        if (SharedCommonUtility.notNullishOrEmpty(foundItem)) {
          return foundItem;
        }
      }
      return undefined;
    };

    const menuBarItem: INavMenuItem = findSubmenuItem(selectedItem);

    return menuBarItem?.children?.filter((child: INavMenuItem): boolean => child.isAvailable) ?? [];
  }

  private buildVerticalNavigationEnabledObservable(): void {
    this.isVerticalNavigationEnabled$ = combineLatest([
      this.featureFlagService.variation$(FeatureFlagCollection.verticalNavigation, false),
      this.userOptinService.hasOptedIn$(UserOptinFeatures.verticalNavigation),
    ]).pipe(
      map(([verticalNavigationFullyEnabled, verticalNavigationOptinEnabled]: [boolean, boolean]): boolean => {
        return verticalNavigationFullyEnabled || verticalNavigationOptinEnabled;
      }),
    );
  }

  public getAvailableSubmenuItems$(triggerItem: NavigationItem): Observable<INavMenuItem[]> {
    return this.activeTopLevelMenuItem$().pipe(
      map((selectedItem: INavMenuItem) => this.getAvailableSubmenusForItem(triggerItem, selectedItem)),
    );
  }

  public get navigationRoutingTree(): INavMenuItem[] {
    return NavigationRoutingTree;
  }

  /**
   * Returns the active top level menu item.
   * Is considered active if itself or any of its descendants matches the active route.
   *
   */
  public activeTopLevelMenuItem$(): Observable<INavMenuItem> {
    return this.topLevelMenuItems$.pipe(
      map((topLevelMenuItems: INavMenuItem[]): INavMenuItem => {
        return topLevelMenuItems.find((topLevelMenuItem: INavMenuItem) => topLevelMenuItem.isActive);
      }),
      AngularUtility.shareRef(),
    );
  }

  public activeSecondLevelMenuItem$(): Observable<INavMenuItem | undefined> {
    const toggleAvailableStateForPortfolio = (secondLevelNavMenuItems: INavMenuItem[]): void => {
      if (SharedCommonUtility.isNullishOrEmpty(secondLevelNavMenuItems)) {
        return;
      }

      const isPortfolioView: boolean = this.router.routerState.snapshot.url.startsWith(`/${Api.portfolio}`);
      const websitesAndAppNavigationItem: INavMenuItem = secondLevelNavMenuItems.find(
        (secondLevelNavMenuItem: INavMenuItem): boolean => secondLevelNavMenuItem.id === NavigationItem.websites_and_apps,
      );

      if (SharedCommonUtility.notNullish(websitesAndAppNavigationItem)) {
        const websitesAndAppsChildren: INavMenuItem[] = websitesAndAppNavigationItem.children ?? [];

        websitesAndAppsChildren.map((websitesAndAppsChild: INavMenuItem): void => {
          const isPortfolio: boolean = isPortfolioView && websitesAndAppsChild.id === NavigationItem.websites_and_apps_portfolio;
          const isNotPortfolio: boolean =
            !isPortfolioView && websitesAndAppsChild.id !== NavigationItem.websites_and_apps_portfolio;
          websitesAndAppsChild.isAvailable = websitesAndAppsChild.isAvailable && (isPortfolio || isNotPortfolio);
        });
      }
    };

    return this.activeTopLevelMenuItem$().pipe(
      map((activeTopLevelMenuItem: INavMenuItem) => {
        const secondLevelNavMenuItems: INavMenuItem[] = activeTopLevelMenuItem?.children ?? [];
        toggleAvailableStateForPortfolio(secondLevelNavMenuItems);
        return secondLevelNavMenuItems.find((secondLevelMenuItem: INavMenuItem) => secondLevelMenuItem.isActive);
      }),
      AngularUtility.shareRef(),
    );
  }

  public activeThirdLevelMenuItem$(): Observable<INavMenuItem | undefined> {
    return this.activeSecondLevelMenuItem$().pipe(
      map((activeSecondLevelMenuItem: INavMenuItem): INavMenuItem => {
        const thirdLevelNavMenuItems: INavMenuItem[] = activeSecondLevelMenuItem?.children ?? [];
        let activeThirdLevelMenuItem: INavMenuItem = thirdLevelNavMenuItems.find(
          (thirdLevelNavMenuItem: INavMenuItem): boolean => thirdLevelNavMenuItem.isActive,
        );
        if (SharedCommonUtility.notNullishOrEmpty(activeThirdLevelMenuItem?.children)) {
          activeThirdLevelMenuItem =
            activeThirdLevelMenuItem.children.find((thirdLevelChild: INavMenuItem): boolean => thirdLevelChild.isActive) ??
            activeThirdLevelMenuItem;
        }
        return activeThirdLevelMenuItem;
      }),
      AngularUtility.shareRef(),
    );
  }

  public activeMenuItemForWorkspaceSwitcher$(): Observable<INavMenuItem | undefined> {
    type IWorkspaceSwitcherProps = [INavMenuItem[], INavMenuItem, INavMenuItem, INavMenuItem];
    return combineLatest([
      this.getTopLevelMenuItems$([NavigationItem.workspace]),
      this.activeTopLevelMenuItem$(),
      this.activeSecondLevelMenuItem$(),
      this.activeThirdLevelMenuItem$(),
    ]).pipe(
      distinctUntilChanged(isEqual),
      map(
        ([
          workspacesMenuItem,
          activeTopLevelMenuItem,
          activeSecondLevelMenuItem,
          activeThirdLevelMenuItem,
        ]: IWorkspaceSwitcherProps): INavMenuItem => {
          if (activeTopLevelMenuItem?.id !== NavigationItem.workspace) {
            return workspacesMenuItem?.[0];
          } else if (
            !SharedCommonUtility.isStringEmpty(activeThirdLevelMenuItem?.routerLink) &&
            activeThirdLevelMenuItem?.isAvailable
          ) {
            return activeThirdLevelMenuItem;
          } else if (
            !SharedCommonUtility.isStringEmpty(activeSecondLevelMenuItem?.routerLink) &&
            activeSecondLevelMenuItem?.isAvailable
          ) {
            return activeSecondLevelMenuItem;
          } else if (
            !SharedCommonUtility.isStringEmpty(activeTopLevelMenuItem?.routerLink) &&
            activeTopLevelMenuItem?.isAvailable
          ) {
            return activeTopLevelMenuItem;
          }

          return workspacesMenuItem?.[0];
        },
      ),
      AngularUtility.shareRef(),
    );
  }

  public hasAccessToMenuItemInWorkspace$(
    menuItem: INavMenuItem,
    workspaceId: string,
    digitalPropertyId: string,
  ): Observable<boolean> {
    return this.userAclService.createCheckAccessForCurrentUser().pipe(
      map((adapter: AclSecurityAdapter): boolean => {
        if (SharedCommonUtility.isNullish(menuItem?.requiredSecurity)) {
          return true;
        }

        const isAdmin: boolean = !this.appConfigService.isTenantEnabled();

        const checkAccess = (_requiredSecurity: IRequiredSecurity): boolean => {
          let result: boolean = false;
          if (_requiredSecurity.entityLevel === SecurityEntityLevel.workspace) {
            result = adapter
              .useRequiredSecurity(_requiredSecurity, isAdmin)
              .useFunctionalActions(_requiredSecurity.functionalActions)
              .useWorkspaceId(workspaceId)
              .check();
          } else if (_requiredSecurity.entityLevel === SecurityEntityLevel.digitalProperty) {
            result = adapter
              .useRequiredSecurity(_requiredSecurity, isAdmin)
              .useFunctionalActions(_requiredSecurity.functionalActions)
              .useWorkspaceId(workspaceId)
              .useDigitalPropertyId(digitalPropertyId)
              .check();
          }
          adapter.reset();
          return result;
        };

        return Array.isArray(menuItem.requiredSecurity)
          ? menuItem.requiredSecurity.some(checkAccess)
          : checkAccess(menuItem.requiredSecurity);
      }),
      AngularUtility.shareRef(),
    );
  }

  public getTopLevelMenuItems$(ids: NavigationItem[] = []): Observable<INavMenuItem[]> {
    const filterMenuItemsByIds = (menuItem: INavMenuItem): boolean =>
      SharedCommonUtility.isNullishOrEmpty(ids) || ids.includes(menuItem.id);

    return this.topLevelMenuItems$.pipe(
      map((topLevelMenuItems: INavMenuItem[]): INavMenuItem[] => topLevelMenuItems.filter(filterMenuItemsByIds)),
    );
  }

  public getProfileMenuItems$(onProfileClick?: () => void): Observable<INavMenuItem[]> {
    const ids$: Observable<NavigationItem[]> = this.isVerticalNavigationEnabled$.pipe(
      map((verticalNavEnabled: boolean): NavigationItem[] => [
        NavigationItem.profile,
        ...(verticalNavEnabled ? [NavigationItem.notifications] : []),
        NavigationItem.logout,
      ]),
    );

    const useModal$: Observable<boolean> = this.featureFlagService
      .variation$(FeatureFlagCollection.userSettingsModal, false)
      .pipe(map(Boolean));

    return combineLatest([ids$, useModal$]).pipe(
      switchMap(([ids, useModal]: [NavigationItem[], boolean]) =>
        this.getTopLevelMenuItems$(ids).pipe(
          map((items: INavMenuItem[]) => {
            const availableItemsById: Map<NavigationItem, INavMenuItem> = new Map(
              items.filter((item: INavMenuItem) => item.isAvailable).map((item: INavMenuItem) => [item.id, item]),
            );
            return ids
              .map((id: NavigationItem) => availableItemsById.get(id) ?? null)
              .filter(SharedCommonUtility.notNullish)
              .map((item: INavMenuItem) => {
                if (item.id === NavigationItem.profile && useModal && onProfileClick) {
                  return { ...item, routerLink: null, onClick: onProfileClick };
                }
                return item;
              });
          }),
        ),
      ),
    );
  }

  /**
   * Determines if the active subtree in the navigation tree has any of the items in the given navItemsIdsToCheck
   * list.
   *
   * @param navItemsIdsToCheck Ids of the navigation items to check in the active subtree of the navigation tree.
   */
  public getNavItemInTheActiveSubtree$(navItemsIdsToCheck: NavigationItem[]): Observable<INavMenuItem | null> {
    const navItemsIdsToCheckSet: Set<NavigationItem> = new Set<NavigationItem>(navItemsIdsToCheck);

    const findIfNavItemIsInTheActiveSubtree = (activeNavItem: INavMenuItem): INavMenuItem | null => {
      if (navItemsIdsToCheckSet.has(activeNavItem.id)) {
        return activeNavItem;
      }

      const activeChild: INavMenuItem = _find(activeNavItem.children ?? [], 'isActive');
      if (SharedCommonUtility.notNullish(activeChild)) {
        return findIfNavItemIsInTheActiveSubtree(activeChild);
      }

      return null;
    };

    return this.activeTopLevelMenuItem$().pipe(
      map((activeTopLevelMenuItem?: INavMenuItem): INavMenuItem =>
        SharedCommonUtility.notNullish(activeTopLevelMenuItem) ? findIfNavItemIsInTheActiveSubtree(activeTopLevelMenuItem) : null,
      ),
    );
  }

  private buildActiveDigitalPropertyFromQueryParams(): void {
    this.activeDigitalPropertyFromQueryParams$ = combineLatest([
      this.activatedRoute.queryParams,
      this.userService.userDataChanged$.pipe(filter(SharedCommonUtility.notNullish)),
    ]).pipe(
      map(([queryParams, user]: [Params, IUserServerResponse]) => {
        const { digitalPropertyId, workspaceId } = LinkedPropertyUtility.fromLinkedPropertyQueryParam(queryParams);
        const digitalProperties: IDigitalPropertyListItem[] = SharedCommonUtility.notNullish(workspaceId)
          ? user[$user.digitalProperties].filter(
              (item: IDigitalPropertyListItem): boolean => item[$digitalProperty.workspace][$workspace._id] === workspaceId,
            )
          : user[$user.digitalProperties];
        return digitalProperties.find(
          (item: IDigitalPropertyListItem): boolean => item[$digitalProperty._id] === digitalPropertyId,
        );
      }),
      AngularUtility.shareRef(),
    );
  }

  private buildNavigationContext(): void {
    this.currentNavigationContext$ = combineLatest([
      this.activeTopLevelMenuItem$(),
      this.activeSecondLevelMenuItem$(),
      this.activeThirdLevelMenuItem$(),
    ]).pipe(
      map(([activeTop, activeSecond, activeThird]: [INavMenuItem, INavMenuItem, INavMenuItem]): NavigationContext => {
        return (
          NavService.getMenuItemContext(activeThird) ||
          NavService.getMenuItemContext(activeSecond) ||
          NavService.getMenuItemContext(activeTop)
        );
      }),
      distinctUntilChanged(),
    );
  }

  private buildNavigationContextItems(): void {
    this.tenantNavigationContextItem$ = combineLatest([
      this.userService.currentTenant$.pipe(filter(SharedCommonUtility.notNullish)),
      this.getNavItemById$(NavigationItem.organization_dashboard),
    ]).pipe(
      map(([tenant, orgNavItem]: [ITenantInfo, INavMenuItem]): INavigationContextItem => {
        if (SharedCommonUtility.isNullish(tenant) || SharedCommonUtility.isNullish(orgNavItem)) {
          return null;
        }

        return {
          id: tenant[$tenant._id],
          type: NavigationContext.tenant,
          icon: Icons.Building,
          label: tenant[$tenant.name],
          routerLink: orgNavItem.isAvailable ? orgNavItem.routerLink : null,
        };
      }),
    );

    this.workspaceNavigationContextItem$ = combineLatest([
      this.activeDigitalPropertyFromQueryParams$,
      this.getNavItemById$(NavigationItem.workspace),
    ]).pipe(
      map(([digitalProperty, workspaceNavItem]: [IDigitalPropertyListItem, INavMenuItem]): INavigationContextItem => {
        if (SharedCommonUtility.isNullish(digitalProperty) || SharedCommonUtility.isNullish(workspaceNavItem)) {
          return null;
        }

        return {
          id: digitalProperty[$digitalProperty.workspace][$workspace._id],
          type: NavigationContext.workspace,
          icon: Icons.Briefcase,
          label: digitalProperty[$digitalProperty.workspace][$workspace.name],
          routerLink: workspaceNavItem.isAvailable ? workspaceNavItem.routerLink : null,
        };
      }),
    );

    this.digitalPropertyNavigationContextItem$ = combineLatest([
      this.activeDigitalPropertyFromQueryParams$,
      this.getNavItemById$(NavigationItem.websites_and_apps_overview),
    ]).pipe(
      switchMap(([digitalProperty, overviewNavItem]: [IDigitalPropertyListItem, INavMenuItem]) => {
        if (SharedCommonUtility.isNullish(digitalProperty)) {
          return of<[IDigitalPropertyListItem, INavMenuItem, Icons]>([null, overviewNavItem, null]);
        }

        return this.digitalPropertyService.getDigitalPropertyTypes().pipe(
          map((types: IDigitalPropertyType[]) => {
            const icon = this.getIconForDigitalPropertyTypeId(digitalProperty[$digitalProperty.typeId], types);
            return [digitalProperty, overviewNavItem, icon];
          }),
        );
      }),
      map(([digitalProperty, dpNavItem, icon]: [IDigitalPropertyListItem, INavMenuItem, Icons]): INavigationContextItem => {
        if (SharedCommonUtility.isNullish(digitalProperty) || SharedCommonUtility.isNullish(dpNavItem)) {
          return null;
        }

        return {
          id: digitalProperty[$digitalProperty._id],
          type: NavigationContext.digitalProperty,
          label: digitalProperty[$digitalProperty.name],
          icon,
          routerLink: dpNavItem.isAvailable ? dpNavItem.routerLink : null,
        };
      }),
    );
  }

  public getCurrentContextItem$(): Observable<INavigationContextItem> {
    return this.currentNavigationContext$.pipe(
      switchMap((context: NavigationContext): Observable<INavigationContextItem> => {
        switch (context) {
          case NavigationContext.digitalProperty:
            return this.digitalPropertyNavigationContextItem$;
          case NavigationContext.workspace:
            return this.workspaceNavigationContextItem$;
          case NavigationContext.tenant:
            return this.tenantNavigationContextItem$;
          default:
            return of(null);
        }
      }),
    );
  }

  public getIconForDigitalPropertyTypeId(typeId: string, types: IDigitalPropertyType[]): Icons {
    const foundType: IDigitalPropertyType = types.find((t: IDigitalPropertyType) => t[$digitalPropertyType._id] === typeId);
    if (!foundType) {
      return null;
    }

    const typeName: string = foundType[$digitalPropertyType.name];

    if (DigitalPropertyTypeUtility.isWebsite(foundType)) {
      return Icons.Globe;
    } else if (DigitalPropertyTypeUtility.isMobile(foundType)) {
      return Icons.Mobile;
    } else if (typeName === DigitalPropertyTypeNames.wireframes) {
      return Icons.Frame;
    } else if (typeName === DigitalPropertyTypeNames.hardware) {
      return Icons.Wrench;
    } else if (typeName === DigitalPropertyTypeNames.other) {
      return Icons.File;
    }
    return null;
  }

  public getNavItemsByIds$(navItemIds: NavigationItem[]): Observable<INavMenuItem[]> {
    const navItemsIdsSet: Set<NavigationItem> = new Set<NavigationItem>(navItemIds);

    const findNavItemsByIds = (foundNavItems: INavMenuItem[], navItems: INavMenuItem[]): INavMenuItem[] => {
      if (foundNavItems.length === navItemsIdsSet.size) {
        return foundNavItems;
      }

      for (const navItem of navItems) {
        if (navItemsIdsSet.has(navItem.id)) {
          foundNavItems.push(navItem);
        }
        if (SharedCommonUtility.notNullishOrEmpty(navItem.children)) {
          findNavItemsByIds(foundNavItems, navItem.children);
        }
      }

      return foundNavItems;
    };

    return this.topLevelMenuItems$.pipe(map((topLevelMenuItems: INavMenuItem[]) => findNavItemsByIds([], topLevelMenuItems)));
  }

  /**
   * Determines if the item navItemsIdToCheck is in the navigation tree
   * @param navItemIdToCheck Id of the navigation item to check in the navigation tree.
   */
  public getNavItemById$(navItemIdToCheck: NavigationItem): Observable<INavMenuItem> {
    return this.getNavItemsByIds$([navItemIdToCheck]).pipe(map((navItems: INavMenuItem[]) => navItems[0]));
  }

  public buildBreadcrumbs$(): Observable<IDsBreadcrumbItem[]> {
    return combineLatest([
      this.currentNavigationContext$,
      this.tenantNavigationContextItem$,
      this.workspaceNavigationContextItem$,
      this.digitalPropertyNavigationContextItem$,
    ]).pipe(
      map(
        ([context, tenantNavigationContextItem, workspaceNavigationContextItem, digitalPropertyNavigationContextItem]: [
          NavigationContext,
          INavigationContextItem,
          INavigationContextItem,
          INavigationContextItem,
        ]) => {
          const breadcrumbs: IDsBreadcrumbItem[] = [];
          if (SharedCommonUtility.notNullish(tenantNavigationContextItem)) {
            breadcrumbs.push({
              label: tenantNavigationContextItem.label,
              rtLink: tenantNavigationContextItem.routerLink,
            });
          }

          if (context === NavigationContext.workspace || context === NavigationContext.digitalProperty) {
            if (SharedCommonUtility.notNullish(workspaceNavigationContextItem)) {
              breadcrumbs.push({
                label: workspaceNavigationContextItem.label,
                rtLink: workspaceNavigationContextItem.routerLink,
              });
            }
          }

          if (
            context === NavigationContext.digitalProperty &&
            SharedCommonUtility.notNullish(digitalPropertyNavigationContextItem)
          ) {
            breadcrumbs.push({
              label: digitalPropertyNavigationContextItem.label,
              rtLink: digitalPropertyNavigationContextItem.routerLink,
              icon: digitalPropertyNavigationContextItem.icon,
            });
          }
          return breadcrumbs;
        },
      ),
      filter(SharedCommonUtility.notNullishOrEmpty),
    );
  }
}
